/**
 * Generic Device / Hardware Pins Extension
 *
 * Adds blocks for reading and writing directly to digital and analog pins,
 * allowing users to interface with any custom hardware device.
 *
 * ── Blocks ──────────────────────────────────────────────────────
 *
 *   [Command]  set pin [PIN] to mode [MODE]
 *   [Command]  set digital pin [PIN] to [STATE]
 *   [Boolean]  digital pin [PIN] is [STATE]?
 *   [Command]  set analog pin [PIN] to [VALUE]
 *   [Reporter] read analog pin [PIN]
 *   [Command]  wait until digital pin [PIN] is [STATE]
 */

import BaseSensorExtension from '../base-sensor';
import formatMessage from 'format-message';
import hardwareUploader from '../../hardware-uploader';

const PIN_MODE_MENU = [
    {text: 'INPUT', value: 'INPUT'},
    {text: 'OUTPUT', value: 'OUTPUT'},
    {text: 'INPUT_PULLUP', value: 'INPUT_PULLUP'}
];

const BAUD_RATE_MENU = [
    {text: '9600', value: '9600'},
    {text: '19200', value: '19200'},
    {text: '38400', value: '38400'},
    {text: '57600', value: '57600'},
    {text: '115200', value: '115200'}
];

class GenericDeviceExtension extends BaseSensorExtension {
    constructor (runtime) {
        super(runtime);

        // ── Live serial buffer ──────────────────────────────────
        this._serialBuffer = '';   // raw incoming text accumulator
        this._serialLines = [];    // complete lines (split on \n)
        this._lastLine = '';       // most recent complete line
        this._hasUnread = false;   // flag for serialAvailable

        this._onSerialData = (e) => {
            this._serialBuffer += e.detail;
            const parts = this._serialBuffer.split('\n');
            this._serialBuffer = parts.pop(); // keep incomplete tail
            for (const part of parts) {
                const line = part.replace(/\r$/, '');
                this._serialLines.push(line);
                this._lastLine = line;
                this._hasUnread = true;
            }
            // Cap buffer at 500 lines
            if (this._serialLines.length > 500) {
                this._serialLines = this._serialLines.slice(-500);
            }
        };

        hardwareUploader.addEventListener('data', this._onSerialData);
    }

    getSensorInfo () {
        return {
            id: 'genericDevice',
            name: formatMessage({
                id: 'gui.extension.genericDevice.name',
                default: 'Generic Hardware'
            }),
            color1: '#4C97FF', // Basic Scratch blue
            color2: '#4280D7',
            color3: '#3373CC',

            blocks: [
                {
                    opcode: 'arduino_setup',
                    blockType: 'hat',
                    text: formatMessage({
                        id: 'genericDevice.blocks.arduinoSetup',
                        default: 'Arduino Setup (Runs Once)'
                    })
                },
                {
                    opcode: 'arduino_loop',
                    blockType: 'hat',
                    text: formatMessage({
                        id: 'genericDevice.blocks.arduinoLoop',
                        default: 'Arduino Loop (Runs Forever)'
                    })
                },
                '---',
                {
                    opcode: 'setPinMode',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'genericDevice.blocks.setPinMode',
                        default: 'set pin [PIN] to mode [MODE]'
                    }),
                    arguments: {
                        PIN: {
                            type: 'number',
                            defaultValue: '2',
                            menu: 'allPinMenu'
                        },
                        MODE: {
                            type: 'string',
                            defaultValue: 'OUTPUT',
                            menu: 'pinModeMenu'
                        }
                    }
                },
                {
                    opcode: 'digitalWrite',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'genericDevice.blocks.digitalWrite',
                        default: 'set digital pin [PIN] to [STATE]'
                    }),
                    arguments: {
                        PIN: {
                            type: 'number',
                            defaultValue: '2',
                            menu: 'digitalPinMenu'
                        },
                        STATE: {
                            type: 'string',
                            defaultValue: 'HIGH',
                            menu: 'highLowMenu'
                        }
                    }
                },
                {
                    opcode: 'digitalRead',
                    blockType: 'Boolean',
                    text: formatMessage({
                        id: 'genericDevice.blocks.digitalRead',
                        default: 'read digital pin [PIN]'
                    }),
                    arguments: {
                        PIN: {
                            type: 'number',
                            defaultValue: '2',
                            menu: 'digitalPinMenu'
                        }
                    }
                },
                {
                    opcode: 'analogWrite',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'genericDevice.blocks.analogWrite',
                        default: 'set analog pin [PIN] to [VALUE]'
                    }),
                    arguments: {
                        PIN: {
                            type: 'number',
                            defaultValue: '9', // Usually a PWM pin
                            menu: 'digitalPinMenu'
                        },
                        VALUE: {
                            type: 'number',
                            defaultValue: '255'
                        }
                    }
                },
                {
                    opcode: 'analogRead',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'genericDevice.blocks.analogRead',
                        default: 'read analog pin [PIN]'
                    }),
                    arguments: {
                        PIN: {
                            type: 'number',
                            defaultValue: 'A0',
                            menu: 'analogPinMenu'
                        }
                    }
                },
                '---',
                {
                    opcode: 'waitUntil',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'genericDevice.blocks.waitUntil',
                        default: 'wait until pin [PIN] is [STATE]'
                    }),
                    arguments: {
                        PIN: {
                            type: 'number',
                            defaultValue: '2',
                            menu: 'digitalPinMenu'
                        },
                        STATE: {
                            type: 'string',
                            defaultValue: 'HIGH',
                            menu: 'highLowMenu'
                        }
                    }
                },
                '---',
                {
                    opcode: 'serialBegin',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'genericDevice.blocks.serialBegin',
                        default: 'initialize serial at [BAUD] baud'
                    }),
                    arguments: {
                        BAUD: {
                            type: 'string',
                            defaultValue: '115200',
                            menu: 'baudRateMenu'
                        }
                    }
                },
                {
                    opcode: 'serialPrint',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'genericDevice.blocks.serialPrint',
                        default: 'serial print [TEXT]'
                    }),
                    arguments: {
                        TEXT: {
                            type: 'string',
                            defaultValue: 'Hello'
                        }
                    }
                },
                {
                    opcode: 'serialPrintln',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'genericDevice.blocks.serialPrintln',
                        default: 'serial print line [TEXT]'
                    }),
                    arguments: {
                        TEXT: {
                            type: 'string',
                            defaultValue: 'Hello'
                        }
                    }
                },
                {
                    opcode: 'serialAvailable',
                    blockType: 'Boolean',
                    text: formatMessage({
                        id: 'genericDevice.blocks.serialAvailable',
                        default: 'serial data available?'
                    })
                },
                {
                    opcode: 'serialRead',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'genericDevice.blocks.serialRead',
                        default: 'serial read string'
                    })
                },
                {
                    opcode: 'serialLastLine',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'genericDevice.blocks.serialLastLine',
                        default: 'serial last line'
                    })
                }
            ],

            menus: {
                pinModeMenu: {
                    acceptReporters: false,
                    items: PIN_MODE_MENU
                },
                baudRateMenu: {
                    acceptReporters: false,
                    items: BAUD_RATE_MENU
                }
            },

            // ── Code templates ──────────────────────────────────
            codeTemplates: [
                {
                    opcode: 'setPinMode',
                    type: 'command',
                    arduino: {
                        code: '',
                        setup: ['pinMode({{PIN}}, {{MODE}});'],
                        includes: []
                    },
                    python: {
                        code: '',
                        setup: ['pin_{{PIN}} = Pin({{PIN}}, Pin.{{MODE}})'],
                        imports: ['from machine import Pin']
                    }
                },
                {
                    opcode: 'digitalWrite',
                    type: 'command',
                    arduino: {
                        code: 'digitalWrite({{PIN}}, {{STATE}});',
                        setup: [],
                        includes: []
                    },
                    python: {
                        code: 'pin_{{PIN}}.value(1 if "{{STATE}}" == "HIGH" else 0)',
                        setup: [],
                        imports: ['from machine import Pin']
                    }
                },
                {
                    opcode: 'digitalRead',
                    type: 'boolean',
                    arduino: {
                        code: 'digitalRead({{PIN}})',
                        setup: [],
                        includes: []
                    },
                    python: {
                        code: 'pin_{{PIN}}.value()',
                        setup: [],
                        imports: ['from machine import Pin']
                    }
                },
                {
                    opcode: 'analogWrite',
                    type: 'command',
                    arduino: {
                        code: 'analogWrite({{PIN}}, {{VALUE}});',
                        setup: [],
                        includes: []
                    },
                    python: {
                        // Micropython PWM uses duty_u16 (0-65535). Standard Arduino is 0-255.
                        // Assuming the value input is 0-255, we map it. Or pass directly if user provides duty cycle.
                        // For generic compatibility, we just map the raw 0-255 VALUE to 0-65535.
                        code: 'pwm_{{PIN}}.duty_u16(int(({{VALUE}}) * 257))',
                        setup: ['pwm_{{PIN}} = PWM(Pin({{PIN}}))'],
                        imports: ['from machine import Pin, PWM']
                    }
                },
                {
                    opcode: 'analogRead',
                    type: 'reporter',
                    arduino: {
                        code: 'analogRead({{PIN}})',
                        setup: [],
                        includes: []
                    },
                    python: {
                        code: 'adc_{{PIN}}.read_u16()',
                        setup: ['adc_{{PIN}} = ADC(Pin({{PIN}}))'],
                        imports: ['from machine import Pin, ADC']
                    }
                },
                {
                    opcode: 'waitUntil',
                    type: 'command',
                    arduino: {
                        code: 'while (digitalRead({{PIN}}) != {{STATE}}) { delay(10); }',
                        setup: [],
                        includes: []
                    },
                    python: {
                        code: 'while pin_{{PIN}}.value() != (1 if "{{STATE}}" == "HIGH" else 0):\n    time.sleep_ms(10)',
                        setup: [],
                        imports: ['from machine import Pin', 'import time']
                    }
                },
                {
                    opcode: 'serialBegin',
                    type: 'command',
                    arduino: {
                        code: '',
                        setup: ['Serial.begin({{BAUD}});'],
                        includes: []
                    },
                    python: {
                        code: '',
                        setup: ['sys.stdout.write("Serial initialized at {{BAUD}} baud\\n")'],
                        imports: ['import sys']
                    }
                },
                {
                    opcode: 'serialPrint',
                    type: 'command',
                    arduino: {
                        code: 'Serial.print({{TEXT}});',
                        setup: [],
                        includes: []
                    },
                    python: {
                        code: 'sys.stdout.write(str({{TEXT}}))',
                        setup: [],
                        imports: ['import sys']
                    }
                },
                {
                    opcode: 'serialPrintln',
                    type: 'command',
                    arduino: {
                        code: 'Serial.println({{TEXT}});',
                        setup: [],
                        includes: []
                    },
                    python: {
                        code: 'print({{TEXT}})',
                        setup: [],
                        imports: []
                    }
                },
                {
                    opcode: 'serialAvailable',
                    type: 'boolean',
                    arduino: {
                        code: '(Serial.available() > 0)',
                        setup: [],
                        includes: []
                    },
                    python: {
                        code: 'select.select([sys.stdin], [], [], 0)[0]',
                        setup: [],
                        imports: ['import sys', 'import select']
                    }
                },
                {
                    opcode: 'serialRead',
                    type: 'reporter',
                    arduino: {
                        code: 'Serial.readString()',
                        setup: [],
                        includes: []
                    },
                    python: {
                        code: 'sys.stdin.read()',
                        setup: [],
                        imports: ['import sys']
                    }
                },
                {
                    opcode: 'serialLastLine',
                    type: 'reporter',
                    arduino: {
                        code: 'Serial.readStringUntil(\'\\n\')',
                        setup: [],
                        includes: []
                    },
                    python: {
                        code: 'sys.stdin.readline().strip()',
                        setup: [],
                        imports: ['import sys']
                    }
                }
            ]
        };
    }

    // ── VM-side block handlers (for stage preview) ──────────────

    arduino_setup (args) {
        return false;
    }

    arduino_loop (args) {
        return false;
    }

    setPinMode (args) {
        return Promise.resolve();
    }

    digitalWrite (args) {
        return Promise.resolve();
    }

    digitalRead (args) {
        return this.simulateDigitalRead() === 1;
    }

    analogWrite (args) {
        return Promise.resolve();
    }

    analogRead (args) {
        return this.simulateAnalogRead();
    }

    waitUntil (args) {
        return Promise.resolve();
    }

    serialBegin (args) {
        // If connected, reconnect at the requested baud rate
        if (hardwareUploader.isConnected) {
            const baud = args.BAUD || '115200';
            return hardwareUploader.disconnect()
                .then(() => hardwareUploader.connect(parseInt(baud, 10)))
                .catch(() => {});
        }
        return Promise.resolve();
    }

    serialPrint (args) {
        if (hardwareUploader.isConnected && args.TEXT !== undefined) {
            return hardwareUploader.write(String(args.TEXT));
        }
        return Promise.resolve();
    }

    serialPrintln (args) {
        if (hardwareUploader.isConnected && args.TEXT !== undefined) {
            return hardwareUploader.write(String(args.TEXT) + '\r\n');
        }
        return Promise.resolve();
    }

    serialAvailable () {
        return this._hasUnread;
    }

    serialRead () {
        if (this._serialLines.length > 0) {
            this._hasUnread = this._serialLines.length > 1;
            return this._serialLines.shift();
        }
        return '';
    }

    serialLastLine () {
        return this._lastLine;
    }
}

export default GenericDeviceExtension;
